import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Signup() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", designation: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  if (user && user !== false && user !== null) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/signup", form);
      setSubmitted(true);
      toast.success("Request submitted — an admin will review it.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 grain-bg">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground font-display font-black flex items-center justify-center text-lg">D</div>
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight">Digitalis CRM</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Client Ops Console</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-5xl lg:text-6xl leading-none font-black">Request team access.</h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Submit your details and an admin will review your request. You'll be able to sign in as soon as it's approved.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Already have access? <Link to="/login" className="text-primary hover:underline">Sign in →</Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 relative">
        {submitted ? (
          <div className="w-full max-w-sm text-center space-y-5" data-testid="signup-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-3xl font-black tracking-tight">Request sent.</h2>
            <p className="text-sm text-muted-foreground">
              Your request has been submitted to the admin team. You'll be notified once it's approved and you can then sign in.
            </p>
            <Button onClick={() => nav("/login")} className="w-full" data-testid="signup-back-to-login">Back to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="signup-form">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Sign up</div>
              <h2 className="text-3xl font-black tracking-tight">Join the team.</h2>
              <p className="text-sm text-muted-foreground mt-1">This request will be reviewed by your admin.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-widest">Full name *</Label>
              <Input id="name" data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest">Work email *</Label>
              <Input id="email" data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@digitalis.co.in" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-widest">Password (min 8) *</Label>
              <Input id="password" data-testid="signup-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Choose a strong password" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest">Designation</Label>
                <Input data-testid="signup-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. SEO Analyst" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest">Phone</Label>
                <Input data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" />
              </div>
            </div>

            {err && <div className="text-sm text-destructive" data-testid="signup-error">{err}</div>}

            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              {loading ? "Sending…" : "Request access →"}
            </Button>

            <div className="text-xs text-muted-foreground border-t border-border pt-4">
              Already approved? <Link to="/login" className="text-primary hover:underline" data-testid="signup-signin-link">Sign in instead</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
